import { 
  collection, 
  query, 
  where, 
  getDocs, 
  serverTimestamp,
  doc,
  setDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Service for tracking completed campaigns per user
 */

export interface CompletedCampaign {
  id?: string;
  userId: string;
  campaignId: string;
  completedAt: Date;
  status: 'win' | 'lose';
}

/**
 * Mark a campaign as completed for a user
 * DEPRECATED: This function is no longer used from the frontend.
 * Campaign completion is now handled by the backend webhook.
 * Keeping this function for backward compatibility and potential future use.
 */
export const markCampaignCompleted = async (
  userId: string, 
  campaignId: string, 
  status: 'win' | 'lose'
): Promise<void> => {
  console.warn('⚠️ markCampaignCompleted called - this should be handled by backend webhook');
  console.log('📝 markCampaignCompleted called with:', { userId, campaignId, status });
  
  try {
    // Use composite document ID to prevent duplicates
    const docId = `${userId}_${campaignId}`;
    console.log('📝 Creating/updating document with ID:', docId);
    
    const completedRef = doc(db, 'completed_campaigns', docId);
    
    const dataToSave = {
      userId,
      campaignId,
      status,
      completedAt: serverTimestamp()
    };
    console.log('📝 Data to save:', dataToSave);
    
    await setDoc(completedRef, dataToSave, { merge: true }); // merge: true will update if exists, create if not
    
    console.log(`✅ Campaign ${campaignId} marked as completed for user ${userId} with status: ${status}`);
  } catch (error) {
    console.error('❌ Error marking campaign as completed:', error);
    console.error('Error details:', {
      userId,
      campaignId,
      status,
      error
    });
    throw error;
  }
};

/**
 * Check if a user has completed a specific campaign
 */
export const hasCompletedCampaign = async (
  userId: string, 
  campaignId: string
): Promise<boolean> => {
  try {
    const docId = `${userId}_${campaignId}`;
    const completedDoc = await getDocs(query(collection(db, 'completed_campaigns'), where('__name__', '==', docId)));
    
    return !completedDoc.empty;
  } catch (error) {
    console.error('Error checking campaign completion:', error);
    return false;
  }
};

/**
 * Get all completed campaign IDs for a user
 * Returns array of campaign IDs
 */
export const getUserCompletedCampaigns = async (
  userId: string
): Promise<string[]> => {
  console.log('📋 Fetching completed campaigns for user:', userId);
  
  try {
    const completedRef = collection(db, 'completed_campaigns');
    const q = query(completedRef, where('userId', '==', userId));
    
    const querySnapshot = await getDocs(q);
    const completedCampaignIds: string[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      console.log('📋 Found completed campaign:', {
        docId: doc.id,
        campaignId: data.campaignId,
        status: data.status,
        completedAt: data.completedAt
      });
      completedCampaignIds.push(data.campaignId);
    });
    
    console.log('📋 Total completed campaigns found:', completedCampaignIds.length);
    console.log('📋 Campaign IDs:', completedCampaignIds);
    
    return completedCampaignIds;
  } catch (error) {
    console.error('❌ Error fetching completed campaigns:', error);
    return [];
  }
};

/**
 * Get detailed completion data for all user's completed campaigns
 */
export const getUserCompletedCampaignsDetailed = async (
  userId: string
): Promise<CompletedCampaign[]> => {
  try {
    const completedRef = collection(db, 'completed_campaigns');
    const q = query(completedRef, where('userId', '==', userId));
    
    const querySnapshot = await getDocs(q);
    const completedCampaigns: CompletedCampaign[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      completedCampaigns.push({
        id: doc.id,
        userId: data.userId,
        campaignId: data.campaignId,
        status: data.status,
        completedAt: data.completedAt?.toDate?.() || new Date(data.completedAt)
      });
    });
    
    return completedCampaigns;
  } catch (error) {
    console.error('Error fetching completed campaigns details:', error);
    return [];
  }
};

